import React, { createContext, useState, useContext, ReactNode } from "react";
import DataIngestion from "app/apiService/dataIngestion/dataIngestionApiService";
import { APIResponseType } from 'app/apiService/types';

// ----------- Type Definitions -----------
interface DataIngestionContextType {
  loading: boolean;
  error: string;
  success: boolean;
  uploads: any[];
  
  setError: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  
  uploadData: (dealerId: string, formData: FormData, callback?: (runId?: string) => void) => Promise<string | undefined>;
  getUploads: (dealerId: string, params?: any) => Promise<void>;
  getUploadStatus: (uploadId: string) => Promise<any>;
}

interface UploadDataParams {
  file: File;
  provider: string;
  lead_type: string;
  start_date: string;
  end_date: string;
}

// ----------- Context Create -----------
export const DataIngestionContext = createContext<DataIngestionContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const DataIngestionProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [uploads, setUploads] = useState<any[]>([]);

  // ----------- Success Handler -----------
  const handleSuccess = (callback?: () => void) => {
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      if (callback) callback();
    }, 2000);
  };

  // ----------- API Calls -----------
  const uploadData = async (dealerId: string, formData: any, callback?: (runId?: string) => void): Promise<string | undefined> => {
    if (!dealerId) {
      setError("No dealer ID provided");
      return;
    }
    
    setLoading(true);
    setError("");
    try {
      const response = await DataIngestion.uploadData(dealerId, formData);
      
      // Check the response status and handle error responses
      if (response.statusText === 'fail' || (response.statusCode !== undefined && response.statusCode >= 400)) {
        const errorMessage = response.message || "Failed to upload data";
        setError(errorMessage);
        return;
      }
      
      // Extract run ID from response
      const runId = response.body?._id || response.body?.run_id || response.body?.id;
      console.log('📋 Upload successful, Run ID:', runId);
      
      handleSuccess(() => callback?.(runId));
      await getUploads(dealerId);
      
      return runId;
    } catch (err: unknown) {
      // Handle API error responses with message
      if (typeof err === 'object' && err !== null && 'message' in err) {
        setError((err as { message: string }).message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to upload data");
      }
      return;
    } finally {
      setLoading(false);
    }
  };

  const getUploads = async (dealerId: string, params?: any) => {
    if (!dealerId) {
      setError("No dealer ID provided");
      return;
    }
    
    setLoading(true);
    setError("");
    try {
      // const response = await DataIngestion.getUploads(dealerId, params);
      // setUploads(response.body);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch uploads");
    } finally {
      setLoading(false);
    }
  };

  const getUploadStatus = async (uploadId: string) => {
    setLoading(true);
    setError("");
    try {
      const response = await DataIngestion.getUploadStatus(uploadId);
      return response.body;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to get upload status");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    loading,
    error,
    success,
    uploads,
    setError,
    setSuccess,
    uploadData,
    getUploads,
    getUploadStatus,
  };

  return <DataIngestionContext.Provider value={value}>{children}</DataIngestionContext.Provider>;
};

// ----------- Custom Hook -----------
export const useDataIngestion = () => {
  const context = useContext(DataIngestionContext);
  if (context === undefined) {
    throw new Error('useDataIngestion must be used within a DataIngestionProvider');
  }
  return context;
}; 